import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import axios from 'axios';
import api from '../api/axios';
// 임시 데이터 가져오기
import { users as mockUsers } from '../data/mockData';

// 초기 상태
const initialState = {
  isAuthenticated: false,
  user: null,
  loading: true,
  error: null,
  message: null,
  redirectPath: '/',
};

// 리듀서
const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
    case 'SOCIAL_LOGIN_SUCCESS':
      localStorage.setItem('token', action.payload.token);
      return {
        ...state,
        isAuthenticated: true,
        loading: false,
        error: null,
      };
    case 'REGISTER_SUCCESS':
      return {
        ...state,
        loading: false,
        error: null,
        message: action.payload.message
      };
    case 'EMAIL_VERIFICATION_SUCCESS':
      return {
        ...state,
        loading: false,
        error: null,
        message: action.payload.message
      };
    case 'USER_LOADED':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload,
        loading: false,
        error: null,
      };
    case 'AUTH_ERROR':
    case 'LOGIN_FAIL':
    case 'REGISTER_FAIL':
    case 'SOCIAL_LOGIN_FAIL':
    case 'LOGOUT':
      localStorage.removeItem('token');
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        loading: false,
        error: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
        message: null
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: true,
      };
    case 'SET_REDIRECT_PATH':
      return {
        ...state,
        redirectPath: action.payload,
      };
    default:
      return state;
  }
};

// 컨텍스트 생성
const AuthContext = createContext();

// 컨텍스트 프로바이더 컴포넌트
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // 사용자 정보 로드 함수
  const loadUser = async () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      dispatch({ type: 'AUTH_ERROR' });
      return;
    }
    
    try {
      // 실제 API 호출
      const res = await api.get('/users/me');
      dispatch({ type: 'USER_LOADED', payload: res.data });
    } catch (err) {
      dispatch({ type: 'AUTH_ERROR' });
    }
  };

  // 토큰이 있으면 사용자 정보 로드
  useEffect(() => {
    loadUser();
  }, []);

  // 로그인 함수
  const login = async (email, password) => {
    // 로그인 시도 전에 에러 상태 초기화
    dispatch({ type: 'CLEAR_ERROR' });
    dispatch({ type: 'SET_LOADING' });
    
    try {
      // 실제 API 호출
      const res = await api.post('/users/login', {
        email,
        password
      });
      
      const { token } = res.data;
      localStorage.setItem('token', token);
      
      dispatch({ type: 'LOGIN_SUCCESS', payload: { token } });
      
      // 사용자 정보 로드
      loadUser();
      return true;
    } catch (err) {
      // 백엔드에서 받은 에러 메시지를 그대로 사용
      const errorMessage = err.response?.data?.msg || '로그인 중 오류가 발생했습니다.';
      dispatch({ 
        type: 'LOGIN_FAIL', 
        payload: errorMessage 
      });
      return false;
    }
  };

  // 회원가입 함수
  const register = async (userData) => {
    dispatch({ type: 'SET_LOADING' });
    
    try {
      // 실제 API 호출
      const res = await api.post('/users/register', {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        password: userData.password,
        birthDate: userData.birthDate,
        gender: userData.gender,
        phoneNumber: userData.phoneNumber,
        nationality: userData.nationality,
        residenceCountry: userData.residenceCountry
      });
      
      // 회원가입 성공 시 토큰을 저장하지 않고 성공 상태만 반환
      dispatch({ 
        type: 'REGISTER_SUCCESS', 
        payload: { message: '회원가입이 완료되었습니다. 이메일 인증 링크를 확인해주세요.' } 
      });
      
      return { success: true, message: '회원가입이 완료되었습니다. 이메일 인증 링크를 확인해주세요.' };
    } catch (err) {
      dispatch({ 
        type: 'REGISTER_FAIL', 
        payload: err.response?.data?.msg || '회원가입 중 오류가 발생했습니다.' 
      });
      return { success: false, message: err.response?.data?.msg || '회원가입 중 오류가 발생했습니다.' };
    }
  };

  // Google 로그인 함수
  const googleLogin = async (tokenId, userData) => {
    dispatch({ type: 'SET_LOADING' });
    
    console.log('Google Login - Token ID:', tokenId ? '있음' : '없음');
    console.log('Google Login - User Data:', userData);
    console.log('API URL:', process.env.REACT_APP_API_URL);
    
    try {
      // 필수 데이터 확인
      if (!tokenId || !userData || !userData.email) {
        console.error('Google Login - 필수 데이터 부족:', { 
          tokenId: !!tokenId, 
          userData: !!userData, 
          email: userData?.email 
        });
        dispatch({ 
          type: 'SOCIAL_LOGIN_FAIL', 
          payload: '구글 로그인에 필요한 정보가 부족합니다.' 
        });
        return { success: false, message: '구글 로그인에 필요한 정보가 부족합니다.' };
      }
      
      // 이름 정보 확인
      let firstName = userData.firstName || userData.given_name || '';
      let lastName = userData.lastName || userData.family_name || '';
      
      // 이름이 없고 전체 이름이 있는 경우 분리 시도
      if ((!firstName || !lastName) && userData.name) {
        const nameParts = userData.name.split(' ');
        if (nameParts.length > 1) {
          lastName = nameParts[0];
          firstName = nameParts.slice(1).join(' ');
        } else {
          firstName = userData.name;
          lastName = '';
        }
      }
      
      // Google ID 확인
      const googleId = userData.googleId || userData.sub;
      
      if (!googleId) {
        console.error('Google Login - Google ID 없음');
        dispatch({ 
          type: 'SOCIAL_LOGIN_FAIL', 
          payload: '구글 계정 ID를 확인할 수 없습니다.' 
        });
        return { success: false, message: '구글 계정 ID를 확인할 수 없습니다.' };
      }
      
      // 사용자 확인 API 호출 (이미 가입된 회원인지 확인)
      console.log('Google Login - 사용자 확인 요청 시작');
      
      try {
        // 이메일로 사용자 확인 요청
        const checkRes = await api.post('/users/check-social-user', {
          email: userData.email,
          provider: 'google',
          providerId: googleId
        });
        
        console.log('Google Login - 사용자 확인 응답:', checkRes.data);
        
        // 응답에 따른 처리
        if (checkRes.data.exists) {
          // 이미 가입된 회원인 경우
          if (checkRes.data.provider === 'google') {
            // 구글로 가입된 회원 - 바로 로그인 처리
            console.log('Google Login - 기존 구글 회원 로그인 처리');
            
            // 로그인 API 호출
            const loginRes = await api.post('/users/auth/google', {
              token: tokenId,
              email: userData.email,
              googleId: googleId
            });
            
            const { token } = loginRes.data;
            
            if (!token) {
              throw new Error('서버에서 인증 토큰을 받지 못했습니다.');
            }
            
            localStorage.setItem('token', token);
            dispatch({ type: 'SOCIAL_LOGIN_SUCCESS', payload: { token } });
            
            // 사용자 정보 로드
            await loadUser();
            return { success: true, isNewUser: false };
          } else {
            // 다른 방식으로 가입된 회원
            dispatch({ 
              type: 'SOCIAL_LOGIN_FAIL', 
              payload: `이미 ${checkRes.data.displayProvider || checkRes.data.provider} 방식으로 가입된 이메일입니다. 해당 방식으로 로그인해주세요.` 
            });
            return { 
              success: false, 
              message: `이미 ${checkRes.data.displayProvider || checkRes.data.provider} 방식으로 가입된 이메일입니다. 해당 방식으로 로그인해주세요.`,
              provider: checkRes.data.provider
            };
          }
        } else {
          // 새 회원인 경우 - 회원가입 처리
          console.log('Google Login - 새 회원 가입 처리');
          
          // 추가 정보가 필요한지 확인
          if (!userData.birthDate || !userData.gender || !userData.phoneNumber || 
              !userData.nationality || !userData.residenceCountry) {
            // 추가 정보가 필요한 경우
            return { 
              success: false, 
              isNewUser: true,
              needsAdditionalInfo: true,
              partialData: {
                email: userData.email,
                firstName,
                lastName,
                googleId,
                provider: 'google',
                token: tokenId
              }
            };
          }
          
          // 모든 정보가 있는 경우 회원가입 진행
          const registerRes = await api.post('/users/auth/google', {
            token: tokenId,
            email: userData.email,
            firstName: firstName,
            lastName: lastName,
            googleId: googleId,
            birthDate: userData.birthDate || '',
            gender: userData.gender || '',
            phoneNumber: userData.phoneNumber || '',
            nationality: userData.nationality || '',
            residenceCountry: userData.residenceCountry || ''
          });
          
          const { token } = registerRes.data;
          
          if (!token) {
            throw new Error('서버에서 인증 토큰을 받지 못했습니다.');
          }
          
          localStorage.setItem('token', token);
          dispatch({ type: 'SOCIAL_LOGIN_SUCCESS', payload: { token } });
          
          // 사용자 정보 로드
          await loadUser();
          return { success: true, isNewUser: true };
        }
      } catch (checkErr) {
        // 사용자 확인 중 오류 발생
        console.error('Google Login - 사용자 확인 오류:', checkErr);
        
        // 서버 오류인 경우 기존 로직으로 진행 (백엔드에 해당 API가 없을 수 있음)
        console.log('Google Login - 기존 로직으로 진행');
        
        // 실제 API 호출
        console.log('Google Login - API 요청 시작:', '/users/auth/google');
        
        // 프록시 설정을 활용하여 API 요청
        const res = await api.post('/users/auth/google', {
          token: tokenId,
          email: userData.email,
          firstName: firstName,
          lastName: lastName,
          googleId: googleId,
          // 추가 정보가 있는 경우 포함
          birthDate: userData.birthDate || '',
          gender: userData.gender || '',
          phoneNumber: userData.phoneNumber || '',
          nationality: userData.nationality || '',
          residenceCountry: userData.residenceCountry || ''
        });
        
        console.log('Google Login - API 응답:', res.data);
        const { token } = res.data;
        
        if (!token) {
          console.error('Google Login - 토큰 없음');
          dispatch({ 
            type: 'SOCIAL_LOGIN_FAIL', 
            payload: '서버에서 인증 토큰을 받지 못했습니다.' 
          });
          return { success: false, message: '서버에서 인증 토큰을 받지 못했습니다.' };
        }
        
        localStorage.setItem('token', token);
        
        dispatch({ type: 'SOCIAL_LOGIN_SUCCESS', payload: { token } });
        
        // 사용자 정보 로드
        console.log('Google Login - 사용자 정보 로드 시작');
        await loadUser();
        console.log('Google Login - 사용자 정보 로드 완료');
        return { success: true };
      }
    } catch (err) {
      console.error('Google login error in AuthContext:', err);
      console.error('Error details:', {
        response: err.response ? {
          status: err.response.status,
          data: err.response.data
        } : 'No response',
        request: err.request ? 'Request sent but no response' : 'No request',
        message: err.message
      });
      
      let errorMessage = 'Google 로그인 중 오류가 발생했습니다.';
      
      if (err.response) {
        // 서버 응답이 있는 경우
        errorMessage = err.response.data?.msg || `서버 오류: ${err.response.status}`;
      } else if (err.request) {
        // 요청은 보냈지만 응답이 없는 경우
        errorMessage = '서버에 연결할 수 없습니다. 백엔드 서버가 실행 중인지 확인해주세요.';
      } else {
        // 요청 설정 중 오류가 발생한 경우
        errorMessage = err.message || errorMessage;
      }
      
      dispatch({ 
        type: 'SOCIAL_LOGIN_FAIL', 
        payload: errorMessage
      });
      return { success: false, message: errorMessage };
    }
  };

  // Kakao 로그인 함수
  const kakaoLogin = async (tokenId, userData) => {
    dispatch({ type: 'SET_LOADING' });
    
    console.log('Kakao Login - Token ID:', tokenId ? '있음' : '없음');
    console.log('Kakao Login - User Data:', userData);
    
    try {
      // 필수 데이터 확인
      if (!tokenId || !userData || !userData.email) {
        console.error('Kakao Login - 필수 데이터 부족:', { 
          tokenId: !!tokenId, 
          userData: !!userData, 
          email: userData?.email 
        });
        dispatch({ 
          type: 'SOCIAL_LOGIN_FAIL', 
          payload: '카카오 로그인에 필요한 정보가 부족합니다.' 
        });
        return { success: false, message: '카카오 로그인에 필요한 정보가 부족합니다.' };
      }
      
      // Kakao ID 확인
      const kakaoId = userData.kakaoId;
      
      if (!kakaoId) {
        console.error('Kakao Login - Kakao ID 없음');
        dispatch({ 
          type: 'SOCIAL_LOGIN_FAIL', 
          payload: '카카오 계정 ID를 확인할 수 없습니다.' 
        });
        return { success: false, message: '카카오 계정 ID를 확인할 수 없습니다.' };
      }
      
      // 사용자 확인 API 호출 (이미 가입된 회원인지 확인)
      console.log('Kakao Login - 사용자 확인 요청 시작');
      
      try {
        // 이메일로 사용자 확인 요청
        const checkRes = await api.post('/users/check-social-user', {
          email: userData.email,
          provider: 'kakao',
          providerId: kakaoId
        });
        
        console.log('Kakao Login - 사용자 확인 응답:', checkRes.data);
        
        // 응답에 따른 처리
        if (checkRes.data.exists) {
          // 이미 가입된 회원인 경우
          if (checkRes.data.provider === 'kakao') {
            // 카카오로 가입된 회원 - 바로 로그인 처리
            console.log('Kakao Login - 기존 카카오 회원 로그인 처리');
            
            // 로그인 API 호출
            const loginRes = await api.post('/users/auth/kakao', {
              token: tokenId,
              email: userData.email,
              kakaoId: kakaoId
            });
            
            const { token } = loginRes.data;
            
            if (!token) {
              throw new Error('서버에서 인증 토큰을 받지 못했습니다.');
            }
            
            localStorage.setItem('token', token);
            dispatch({ type: 'SOCIAL_LOGIN_SUCCESS', payload: { token } });
            
            // 사용자 정보 로드
            await loadUser();
            return { success: true, isNewUser: false };
          } else {
            // 다른 방식으로 가입된 회원
            dispatch({ 
              type: 'SOCIAL_LOGIN_FAIL', 
              payload: `이미 ${checkRes.data.displayProvider || checkRes.data.provider} 방식으로 가입된 이메일입니다. 해당 방식으로 로그인해주세요.` 
            });
            return { 
              success: false, 
              message: `이미 ${checkRes.data.displayProvider || checkRes.data.provider} 방식으로 가입된 이메일입니다. 해당 방식으로 로그인해주세요.`,
              provider: checkRes.data.provider
            };
          }
        } else {
          // 새 회원인 경우 - 회원가입 처리
          console.log('Kakao Login - 새 회원 가입 처리');
          
          // 추가 정보가 필요한지 확인
          if (!userData.birthDate || !userData.gender || !userData.phoneNumber || 
              !userData.nationality || !userData.residenceCountry) {
            // 추가 정보가 필요한 경우
            return { 
              success: false, 
              isNewUser: true,
              needsAdditionalInfo: true,
              partialData: {
                email: userData.email,
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                kakaoId,
                provider: 'kakao',
                token: tokenId
              }
            };
          }
          
          // 모든 정보가 있는 경우 회원가입 진행
          const registerRes = await api.post('/users/auth/kakao', {
            token: tokenId,
            email: userData.email,
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            kakaoId: kakaoId,
            birthDate: userData.birthDate || '',
            gender: userData.gender || '',
            phoneNumber: userData.phoneNumber || '',
            nationality: userData.nationality || '',
            residenceCountry: userData.residenceCountry || ''
          });
          
          const { token } = registerRes.data;
          
          if (!token) {
            throw new Error('서버에서 인증 토큰을 받지 못했습니다.');
          }
          
          localStorage.setItem('token', token);
          dispatch({ type: 'SOCIAL_LOGIN_SUCCESS', payload: { token } });
          
          // 사용자 정보 로드
          await loadUser();
          return { success: true, isNewUser: true };
        }
      } catch (checkErr) {
        // 사용자 확인 중 오류 발생
        console.error('Kakao Login - 사용자 확인 오류:', checkErr);
        
        // 서버 오류인 경우 기존 로직으로 진행 (백엔드에 해당 API가 없을 수 있음)
        console.log('Kakao Login - 기존 로직으로 진행');
        
        // 실제 API 호출
        console.log('Kakao Login - API 요청 시작:', '/users/auth/kakao');
        
        // 프록시 설정을 활용하여 API 요청
        const res = await api.post('/users/auth/kakao', {
          token: tokenId,
          email: userData.email,
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          kakaoId: kakaoId,
          birthDate: userData.birthDate || '',
          gender: userData.gender || '',
          phoneNumber: userData.phoneNumber || '',
          nationality: userData.nationality || '',
          residenceCountry: userData.residenceCountry || ''
        });
        
        console.log('Kakao Login - API 응답:', res.data);
        const { token } = res.data;
        
        if (!token) {
          console.error('Kakao Login - 토큰 없음');
          dispatch({ 
            type: 'SOCIAL_LOGIN_FAIL', 
            payload: '서버에서 인증 토큰을 받지 못했습니다.' 
          });
          return { success: false, message: '서버에서 인증 토큰을 받지 못했습니다.' };
        }
        
        localStorage.setItem('token', token);
        
        dispatch({ type: 'SOCIAL_LOGIN_SUCCESS', payload: { token } });
        
        // 사용자 정보 로드
        console.log('Kakao Login - 사용자 정보 로드 시작');
        await loadUser();
        console.log('Kakao Login - 사용자 정보 로드 완료');
        return { success: true };
      }
    } catch (err) {
      console.error('Kakao login error in AuthContext:', err);
      
      let errorMessage = 'Kakao 로그인 중 오류가 발생했습니다.';
      
      if (err.response) {
        // 서버 응답이 있는 경우
        errorMessage = err.response.data?.msg || `서버 오류: ${err.response.status}`;
      } else if (err.request) {
        // 요청은 보냈지만 응답이 없는 경우
        errorMessage = '서버에 연결할 수 없습니다. 백엔드 서버가 실행 중인지 확인해주세요.';
      } else {
        // 요청 설정 중 오류가 발생한 경우
        errorMessage = err.message || errorMessage;
      }
      
      dispatch({ 
        type: 'SOCIAL_LOGIN_FAIL', 
        payload: errorMessage
      });
      return { success: false, message: errorMessage };
    }
  };

  // Naver 로그인 함수
  const naverLogin = async (tokenId, userData) => {
    dispatch({ type: 'SET_LOADING' });
    
    console.log('Naver Login - Token ID:', tokenId ? '있음' : '없음');
    console.log('Naver Login - User Data:', userData);
    
    try {
      // 필수 데이터 확인
      if (!tokenId || !userData || !userData.email) {
        console.error('Naver Login - 필수 데이터 부족:', { 
          tokenId: !!tokenId, 
          userData: !!userData, 
          email: userData?.email 
        });
        dispatch({ 
          type: 'SOCIAL_LOGIN_FAIL', 
          payload: '네이버 로그인에 필요한 정보가 부족합니다.' 
        });
        return { success: false, message: '네이버 로그인에 필요한 정보가 부족합니다.' };
      }
      
      // Naver ID 확인
      const naverId = userData.naverId;
      
      if (!naverId) {
        console.error('Naver Login - Naver ID 없음');
        dispatch({ 
          type: 'SOCIAL_LOGIN_FAIL', 
          payload: '네이버 계정 ID를 확인할 수 없습니다.' 
        });
        return { success: false, message: '네이버 계정 ID를 확인할 수 없습니다.' };
      }
      
      // 사용자 확인 API 호출 (이미 가입된 회원인지 확인)
      console.log('Naver Login - 사용자 확인 요청 시작');
      
      try {
        // 이메일로 사용자 확인 요청
        const checkRes = await api.post('/users/check-social-user', {
          email: userData.email,
          provider: 'naver',
          providerId: naverId
        });
        
        console.log('Naver Login - 사용자 확인 응답:', checkRes.data);
        
        // 응답에 따른 처리
        if (checkRes.data.exists) {
          // 이미 가입된 회원인 경우
          if (checkRes.data.provider === 'naver') {
            // 네이버로 가입된 회원 - 바로 로그인 처리
            console.log('Naver Login - 기존 네이버 회원 로그인 처리');
            
            // 로그인 API 호출
            const loginRes = await api.post('/users/auth/naver', {
              token: tokenId,
              email: userData.email,
              naverId: naverId
            });
            
            const { token } = loginRes.data;
            
            if (!token) {
              throw new Error('서버에서 인증 토큰을 받지 못했습니다.');
            }
            
            localStorage.setItem('token', token);
            dispatch({ type: 'SOCIAL_LOGIN_SUCCESS', payload: { token } });
            
            // 사용자 정보 로드
            await loadUser();
            return { success: true, isNewUser: false };
          } else {
            // 다른 방식으로 가입된 회원
            dispatch({ 
              type: 'SOCIAL_LOGIN_FAIL', 
              payload: `이미 ${checkRes.data.displayProvider || checkRes.data.provider} 방식으로 가입된 이메일입니다. 해당 방식으로 로그인해주세요.` 
            });
            return { 
              success: false, 
              message: `이미 ${checkRes.data.displayProvider || checkRes.data.provider} 방식으로 가입된 이메일입니다. 해당 방식으로 로그인해주세요.`,
              provider: checkRes.data.provider
            };
          }
        } else {
          // 새 회원인 경우 - 회원가입 처리
          console.log('Naver Login - 새 회원 가입 처리');
          
          // 추가 정보가 필요한지 확인
          if (!userData.birthDate || !userData.gender || !userData.phoneNumber || 
              !userData.nationality || !userData.residenceCountry) {
            // 추가 정보가 필요한 경우
            return { 
              success: false, 
              isNewUser: true,
              needsAdditionalInfo: true,
              partialData: {
                email: userData.email,
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                naverId,
                provider: 'naver',
                token: tokenId
              }
            };
          }
          
          // 모든 정보가 있는 경우 회원가입 진행
          const registerRes = await api.post('/users/auth/naver', {
            token: tokenId,
            email: userData.email,
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            naverId: naverId,
            birthDate: userData.birthDate || '',
            gender: userData.gender || '',
            phoneNumber: userData.phoneNumber || '',
            nationality: userData.nationality || '',
            residenceCountry: userData.residenceCountry || ''
          });
          
          const { token } = registerRes.data;
          
          if (!token) {
            throw new Error('서버에서 인증 토큰을 받지 못했습니다.');
          }
          
          localStorage.setItem('token', token);
          dispatch({ type: 'SOCIAL_LOGIN_SUCCESS', payload: { token } });
          
          // 사용자 정보 로드
          await loadUser();
          return { success: true, isNewUser: true };
        }
      } catch (checkErr) {
        // 사용자 확인 중 오류 발생
        console.error('Naver Login - 사용자 확인 오류:', checkErr);
        
        // 서버 오류인 경우 기존 로직으로 진행 (백엔드에 해당 API가 없을 수 있음)
        console.log('Naver Login - 기존 로직으로 진행');
        
        // 실제 API 호출
        console.log('Naver Login - API 요청 시작:', '/users/auth/naver');
        
        // 프록시 설정을 활용하여 API 요청
        const res = await api.post('/users/auth/naver', {
          token: tokenId,
          email: userData.email,
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          naverId: naverId,
          birthDate: userData.birthDate || '',
          gender: userData.gender || '',
          phoneNumber: userData.phoneNumber || '',
          nationality: userData.nationality || '',
          residenceCountry: userData.residenceCountry || ''
        });
        
        console.log('Naver Login - API 응답:', res.data);
        const { token } = res.data;
        
        if (!token) {
          console.error('Naver Login - 토큰 없음');
          dispatch({ 
            type: 'SOCIAL_LOGIN_FAIL', 
            payload: '서버에서 인증 토큰을 받지 못했습니다.' 
          });
          return { success: false, message: '서버에서 인증 토큰을 받지 못했습니다.' };
        }
        
        localStorage.setItem('token', token);
        
        dispatch({ type: 'SOCIAL_LOGIN_SUCCESS', payload: { token } });
        
        // 사용자 정보 로드
        console.log('Naver Login - 사용자 정보 로드 시작');
        await loadUser();
        console.log('Naver Login - 사용자 정보 로드 완료');
        return { success: true };
      }
    } catch (err) {
      console.error('Naver login error in AuthContext:', err);
      
      let errorMessage = 'Naver 로그인 중 오류가 발생했습니다.';
      
      if (err.response) {
        // 서버 응답이 있는 경우
        errorMessage = err.response.data?.msg || `서버 오류: ${err.response.status}`;
      } else if (err.request) {
        // 요청은 보냈지만 응답이 없는 경우
        errorMessage = '서버에 연결할 수 없습니다. 백엔드 서버가 실행 중인지 확인해주세요.';
      } else {
        // 요청 설정 중 오류가 발생한 경우
        errorMessage = err.message || errorMessage;
      }
      
      dispatch({ 
        type: 'SOCIAL_LOGIN_FAIL', 
        payload: errorMessage
      });
      return { success: false, message: errorMessage };
    }
  };

  // 로그아웃 함수
  const logout = (currentPath) => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    dispatch({ type: 'LOGOUT' });
    
    // 인증이 필요한 페이지 목록
    const privatePages = [
      '/dashboard',
      '/admin',
      '/learning'
    ];
    
    // 현재 페이지가 인증이 필요한 페이지인지 확인
    const isPrivatePage = privatePages.some(page => currentPath.startsWith(page));
    
    // 인증이 필요한 페이지면 홈으로, 아니면 현재 페이지 유지
    return isPrivatePage ? '/' : currentPath;
  };

  // 에러 초기화 함수
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // 리디렉션 경로 설정 함수
  const setRedirectPath = useCallback((path) => {
    // 현재 리다이렉트 경로와 동일하면 업데이트하지 않음
    if (state.redirectPath !== path) {
      dispatch({ type: 'SET_REDIRECT_PATH', payload: path });
    }
  }, [state.redirectPath]);

  // 비밀번호 재설정 요청 함수
  const requestPasswordReset = async (email) => {
    dispatch({ type: 'SET_LOADING' });
    
    try {
      // 실제 API 호출
      const res = await api.post('/users/forgot-password', { email });
      
      return { success: true, message: res.data.msg || '비밀번호 재설정 링크가 이메일로 전송되었습니다.' };
    } catch (err) {
      dispatch({ 
        type: 'AUTH_ERROR', 
        payload: err.response?.data?.msg || '비밀번호 재설정 요청 중 오류가 발생했습니다.' 
      });
      return { success: false, message: err.response?.data?.msg || '비밀번호 재설정 요청 중 오류가 발생했습니다.' };
    }
  };

  // 비밀번호 재설정 토큰 검증 함수
  const verifyResetToken = async (token) => {
    try {
      // 실제 API 호출
      const res = await api.get(`/users/verify-reset-token/${token}`);
      
      return { success: true };
    } catch (err) {
      return { 
        success: false, 
        message: err.response?.data?.msg || '유효하지 않거나 만료된 토큰입니다.' 
      };
    }
  };

  // 비밀번호 재설정 함수
  const resetPassword = async (token, password) => {
    dispatch({ type: 'SET_LOADING' });
    
    try {
      // 실제 API 호출
      const res = await api.post(`/users/reset-password/${token}`, { password });
      
      return { success: true, message: res.data.msg || '비밀번호가 성공적으로 재설정되었습니다.' };
    } catch (err) {
      dispatch({ 
        type: 'AUTH_ERROR', 
        payload: err.response?.data?.msg || '비밀번호 재설정 중 오류가 발생했습니다.' 
      });
      return { success: false, message: err.response?.data?.msg || '비밀번호 재설정 중 오류가 발생했습니다.' };
    }
  };

  // 이메일 인증 함수
  const verifyEmail = async (token) => {
    dispatch({ type: 'SET_LOADING' });
    
    try {
      // 실제 API 호출
      const res = await api.get(`/users/verify-email/${token}`);
      
      dispatch({ 
        type: 'EMAIL_VERIFICATION_SUCCESS', 
        payload: { message: res.data.msg || '이메일 인증이 완료되었습니다. 로그인해주세요.' } 
      });
      
      return { success: true, message: res.data.msg || '이메일 인증이 완료되었습니다. 로그인해주세요.' };
    } catch (err) {
      dispatch({ 
        type: 'AUTH_ERROR', 
        payload: err.response?.data?.msg || '이메일 인증 중 오류가 발생했습니다.' 
      });
      return { success: false, message: err.response?.data?.msg || '이메일 인증 중 오류가 발생했습니다.' };
    }
  };

  // 이메일 인증 재전송 함수
  const resendVerificationEmail = async (email) => {
    dispatch({ type: 'SET_LOADING' });
    
    try {
      // 실제 API 호출
      const res = await api.post('/users/resend-verification', { email });
      
      return { success: true, message: res.data.msg || '인증 이메일이 재전송되었습니다.' };
    } catch (err) {
      dispatch({ 
        type: 'AUTH_ERROR', 
        payload: err.response?.data?.msg || '인증 이메일 재전송 중 오류가 발생했습니다.' 
      });
      return { success: false, message: err.response?.data?.msg || '인증 이메일 재전송 중 오류가 발생했습니다.' };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        loading: state.loading,
        error: state.error,
        message: state.message,
        redirectPath: state.redirectPath,
        login,
        register,
        googleLogin,
        kakaoLogin,
        naverLogin,
        logout,
        clearError,
        setRedirectPath,
        requestPasswordReset,
        verifyResetToken,
        resetPassword,
        verifyEmail,
        resendVerificationEmail
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// 커스텀 훅
export const useAuth = () => useContext(AuthContext);

export default AuthContext; 